import { Link } from "react-router-dom";
import type { Task } from "../types";
import { PriorityBadge } from "./PriorityBadge";
import { StatusBadge } from "./StatusBadge";

interface TaskCardProps {
	task: Task;
	onDelete?: (id: string) => void;
}

function formatDate(dateString: string | null): string {
	if (!dateString) return "-";
	const date = new Date(dateString);
	return date.toLocaleDateString("ja-JP", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

function isOverdue(dueDate: string | null, status: string): boolean {
	if (!dueDate || status === "done") return false;
	return new Date(dueDate) < new Date();
}

export function TaskCard({ task, onDelete }: TaskCardProps) {
	const overdue = isOverdue(task.dueDate, task.status);

	const handleDelete = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (onDelete && window.confirm("このタスクを削除しますか?")) {
			onDelete(task.id);
		}
	};

	return (
		<Link
			to={`/tasks/${task.id}`}
			className="card block p-4 hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
			aria-label={`タスク: ${task.title}`}
		>
			<div className="flex flex-col gap-3">
				<div className="flex items-start justify-between gap-2">
					<h3 className="text-lg font-semibold text-gray-900 line-clamp-2 flex-1">{task.title}</h3>
					{onDelete && (
						<button
							type="button"
							onClick={handleDelete}
							className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
							aria-label={`${task.title}を削除`}
						>
							<svg
								className="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
								/>
							</svg>
						</button>
					)}
				</div>

				{task.description && (
					<p className="text-gray-600 text-sm line-clamp-2">{task.description}</p>
				)}

				<div className="flex flex-wrap items-center gap-2">
					<StatusBadge status={task.status} size="sm" />
					<PriorityBadge priority={task.priority} size="sm" />
				</div>

				<div className="flex items-center justify-between text-sm text-gray-500">
					<span className={overdue ? "text-red-600 font-medium" : ""}>
						{task.dueDate ? (
							<>
								{overdue && (
									<span className="mr-1" aria-label="期限切れ">
										!
									</span>
								)}
								期限: {formatDate(task.dueDate)}
							</>
						) : (
							"期限なし"
						)}
					</span>
					<span>作成: {formatDate(task.createdAt)}</span>
				</div>
			</div>
		</Link>
	);
}
